import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import Head from 'next/head';
import { PageHead } from '@/components/Common/Seo/PageHead';
import { CompaniesDirectoryView } from '@/components/Directory/CompaniesDirectoryView';
import { NO_STORE_CACHE_CONTROL } from '@/constants/cache';
import { SITE_ORIGIN } from '@/constants/site';
import type { CompanySeoRecord } from '@/utils/seo/company-directory';
import {
  getCompanyDirectoryPage,
  getCompanyDirectoryTotalPages,
} from '@/utils/seo/company-directory-data';

type Props = {
  companies: CompanySeoRecord[];
  hasNextPage: boolean;
  totalPages: number | null;
};

export const getServerSideProps: GetServerSideProps<Props> = async ({ res }) => {
  try {
    const [{ companies, hasNextPage }, totalPages] = await Promise.all([
      getCompanyDirectoryPage(1),
      getCompanyDirectoryTotalPages(),
    ]);

    return {
      props: { companies, hasNextPage, totalPages: totalPages ?? null },
    };
  } catch (error) {
    res.setHeader('Cache-Control', NO_STORE_CACHE_CONTROL);
    throw error;
  }
};
const title = 'B2B Companies Directory: Verified Contacts & Emails';
const description =
  'Browse companies and find verified emails and mobile numbers for the decision-makers you want to reach. Search our directory by industry or location.';

export default function CompaniesPage({
  companies,
  hasNextPage,
  totalPages,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@id': `${SITE_ORIGIN}/companies#breadcrumb`,
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', item: `${SITE_ORIGIN}/`, name: 'Home', position: 1 },
          { '@type': 'ListItem', item: `${SITE_ORIGIN}/companies`, name: 'Companies', position: 2 },
        ],
      },
      {
        '@id': `${SITE_ORIGIN}/companies#itemlist`,
        '@type': 'ItemList',
        itemListElement: companies.map((company, index) => ({
          '@type': 'ListItem',
          item: `${SITE_ORIGIN}/companies/${company.publicId}${company.slug ? `/${company.slug}` : ''}`,
          name: company.name,
          position: index + 1,
        })),
        name: 'Companies Directory',
        numberOfItems: companies.length,
      },
    ],
  };

  return (
    <>
      <PageHead description={description} path="/companies" title={title} />

      <Head>
        {hasNextPage && <link href={`${SITE_ORIGIN}/companies/page/2`} rel="next" />}
        <script
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
          key="companies-directory-ld+json"
          type="application/ld+json"
        />
      </Head>

      <CompaniesDirectoryView
        companies={companies}
        hasNextPage={hasNextPage}
        page={1}
        totalPages={totalPages ?? undefined}
      />
    </>
  );
}
